import { useState } from 'react'
import { api } from '../lib/api'
import { getLocationProvider, type PermissionState } from '../lib/location'
import { requestNotificationPermission } from '../lib/notify'
import { FREE_LIMIT, useStore } from '../lib/store'
import './Onboarding.css'

type Step = 0 | 1 | 2 | 3

export function Onboarding() {
  const { signIn, signUp, startWatching, completeOnboarding } = useStore()
  // Resume rather than restart. This component now stays mounted after sign-up
  // (see lib/onboarding.ts), so a signed-in-but-unfinished user must land on the
  // permission steps, not back on a sign-in form they already completed.
  const [step, setStep] = useState<Step>(() => (useStore.getState().user ? 1 : 0))
  // 'denied' is the case worth separating: no amount of tapping Enable will
  // re-prompt once a browser has recorded a refusal, so the copy has to send
  // the user to their own settings instead of looping them through a button
  // that can no longer do anything.
  const [permission, setPermission] = useState<PermissionState | null>(null)
  const [mode, setMode] = useState<'up' | 'in'>('up')
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [name, setName] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  // The reply is deliberately identical whether or not the address has an
  // account, so this is a notice rather than a confirmation.
  const [resetNotice, setResetNotice] = useState<string | null>(null)

  async function submit() {
    setBusy(true)
    setError(null)
    try {
      if (mode === 'up') await signUp(email.trim(), password, name.trim() || 'You')
      else await signIn(email.trim(), password)
      setStep(1)
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e))
    } finally {
      setBusy(false)
    }
  }

  async function forgot() {
    const address = email.trim()
    if (!address) {
      setError('Enter your email address first, then tap Forgot password.')
      return
    }
    setBusy(true)
    setError(null)
    try {
      const { detail } = await api.forgotPassword(address)
      setResetNotice(detail)
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e))
    } finally {
      setBusy(false)
    }
  }

  async function askLocation() {
    setError(null)
    const provider = getLocationProvider()
    try {
      // The prompt has to come from here, inside a click handler. Desktop
      // browsers — Safari on macOS especially — are far more reluctant to raise
      // a permission dialog for a bare `watchPosition` on load than for one a
      // user visibly asked for, which is why the MacBook flow never showed one
      // (APP-12).
      await provider.getCurrent()
      startWatching()
      setPermission('granted')
      setStep(2)
    } catch (e) {
      // Not fatal — nothing here blocks the app. But we do stop and say what
      // happened, because silently advancing is how the original report ended
      // with "it never asked" and no way to find out why.
      setPermission(await provider.getPermission())
      setError(e instanceof Error ? e.message : String(e))
    }
  }

  async function askNotifications() {
    await requestNotificationPermission()
    setStep(3)
  }

  return (
    <div className="ob">
      {step === 0 && (
        <div className="ob__pane">
          <Scene />
          <div className="ob__copy">
            <h1>
              Never forget
              <br />a stop again.
            </h1>
            <p>Nudge reminds you when you&rsquo;re near the places that matter.</p>
          </div>

          <div className="ob__form glass">
            <div className="ob__tabs">
              <button
                className={mode === 'up' ? 'is-on' : ''}
                onClick={() => {
                  setMode('up')
                  setResetNotice(null)
                }}
              >
                Create account
              </button>
              <button
                className={mode === 'in' ? 'is-on' : ''}
                onClick={() => {
                  setMode('in')
                  setResetNotice(null)
                }}
              >
                Sign in
              </button>
            </div>

            {mode === 'up' && (
              <input
                placeholder="Your name"
                value={name}
                autoComplete="name"
                onChange={(e) => setName(e.target.value)}
              />
            )}
            <input
              placeholder="you@example.com"
              type="email"
              value={email}
              autoComplete="email"
              onChange={(e) => setEmail(e.target.value)}
            />
            <input
              placeholder="Password (8+ characters)"
              type="password"
              value={password}
              autoComplete={mode === 'up' ? 'new-password' : 'current-password'}
              onChange={(e) => setPassword(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && void submit()}
            />

            {error && <p className="ob__error">{error}</p>}
            {resetNotice && <p className="ob__notice">{resetNotice}</p>}

            <button
              className="btn btn--amber"
              disabled={busy || !email.trim() || password.length < 8}
              onClick={submit}
            >
              {busy ? 'One moment…' : 'Get Started'}
            </button>
            {mode === 'in' && (
              <button className="ob__link" disabled={busy} onClick={forgot}>
                Forgot password?
              </button>
            )}
            <p className="ob__fine">Free forever, up to {FREE_LIMIT} active reminders</p>
          </div>
        </div>
      )}

      {step === 1 && (
        <div className="ob__pane ob__pane--center">
          <span className="ob__badge ob__badge--teal">
            <svg width="46" height="46" viewBox="0 0 24 24" fill="none" stroke="var(--teal-deep)" strokeWidth="1.6">
              <path d="M12 21s7-6.3 7-11a7 7 0 1 0-14 0c0 4.7 7 11 7 11Z" />
              <circle cx="12" cy="10" r="2.6" />
            </svg>
          </span>
          <h1>Let Nudge know where you are</h1>
          <p>
            We only use your location to trigger reminders near places you choose.
            It stays on your device and our server, and is never sold or shared.
          </p>
          {error && <p className="ob__error">{error}</p>}
          {permission === 'denied' && (
            <p className="ob__note">
              Your browser is blocking location for this site, so Nudge can&rsquo;t
              ask again from here. Open the padlock (or the location icon) in the
              address bar, set Location to Allow, then reload. On macOS also check
              System Settings &rsaquo; Privacy &amp; Security &rsaquo; Location
              Services for your browser.
            </p>
          )}
          {permission === 'unsupported' && (
            <p className="ob__note">
              This browser has no location support. Reminders will still save, and
              will start triggering in a browser that does.
            </p>
          )}
          <div className="ob__actions">
            {permission !== 'denied' && permission !== 'unsupported' && (
              <button className="btn btn--primary" onClick={askLocation}>
                {error ? 'Try again' : 'Enable Location'}
              </button>
            )}
            <button
              className={`btn ${permission === 'denied' || permission === 'unsupported' ? 'btn--primary' : 'btn--ghost'}`}
              onClick={() => setStep(2)}
            >
              {error ? 'Continue anyway' : 'Not now'}
            </button>
          </div>
        </div>
      )}

      {step === 2 && (
        <div className="ob__pane ob__pane--center">
          <span className="ob__badge ob__badge--pink">
            <svg width="44" height="44" viewBox="0 0 24 24" fill="none" stroke="#b268d8" strokeWidth="1.6">
              <path d="M18 8a6 6 0 1 0-12 0c0 6-2 7-2 7h16s-2-1-2-7Z" />
              <path d="M10.3 20a2 2 0 0 0 3.4 0" />
            </svg>
          </span>
          <h1>Get nudged at the perfect time</h1>
          <p>We&rsquo;ll notify you when you&rsquo;re near your saved places.</p>
          <div className="ob__actions">
            <button className="btn btn--primary" onClick={askNotifications}>
              Enable Notifications
            </button>
            <button className="btn btn--ghost" onClick={() => setStep(3)}>
              Skip for now
            </button>
          </div>
        </div>
      )}

      {step === 3 && (
        <div className="ob__pane ob__pane--center">
          <span className="ob__badge ob__badge--teal">
            <svg width="44" height="44" viewBox="0 0 24 24" fill="none" stroke="var(--teal-deep)" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
              <path d="M4 12.5 9.5 18 20 6.5" />
            </svg>
          </span>
          <h1>One thing to know</h1>
          <p>
            On the web, Nudge can only check your location while it&rsquo;s open on
            screen — browsers don&rsquo;t allow background location, on any platform.
            Keep it open on a drive and it works. The native app will lift that.
          </p>
          <div className="ob__actions">
            <button
              className="btn btn--primary"
              onClick={() => {
                // Marks this browser done *and* releases App's gate — until this
                // runs, Onboarding owns the screen.
                completeOnboarding()
                useStore.setState({ tab: 'list' })
              }}
            >
              Start using Nudge
            </button>
          </div>
        </div>
      )}

      {step > 0 && (
        <div className="ob__dots" aria-hidden="true">
          {[1, 2, 3].map((n) => (
            <i key={n} className={n === step ? 'is-on' : ''} />
          ))}
        </div>
      )}
    </div>
  )
}

/** The road-and-pins illustration from the onboarding mockup, as pure SVG. */
function Scene() {
  return (
    <svg className="ob__scene" viewBox="0 0 400 260" fill="none" aria-hidden="true">
      <defs>
        <linearGradient id="hillA" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#b7e7c9" />
          <stop offset="100%" stopColor="#8fd6b4" />
        </linearGradient>
        <linearGradient id="hillB" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#cdeadd" />
          <stop offset="100%" stopColor="#a9dcc8" />
        </linearGradient>
        <linearGradient id="road" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#dfeef3" />
          <stop offset="100%" stopColor="#f4fbfd" />
        </linearGradient>
      </defs>

      <path d="M0 150c60-30 110 6 170-10s150-40 230-6v126H0Z" fill="url(#hillB)" />
      <path d="M0 178c70-24 130 10 200-4s130-30 200-8v94H0Z" fill="url(#hillA)" />
      <path
        d="M155 260c0-60 28-84 40-104s10-38 10-38h30s-2 24 8 44 42 42 42 98Z"
        fill="url(#road)"
      />

      {[
        { x: 108, y: 96, c: '#f2607d' },
        { x: 296, y: 74, c: '#f5a623' },
        { x: 318, y: 150, c: '#12b8a6' },
      ].map((p, i) => (
        <g key={p.x} style={{ animation: `float 3.4s ${i * 0.5}s ease-in-out infinite` }}>
          <path
            d={`M${p.x} ${p.y + 34}c0 0 13-14 13-23a13 13 0 1 0-26 0c0 9 13 23 13 23Z`}
            fill={p.c}
          />
          <circle cx={p.x} cy={p.y + 11} r="5" fill="#fff" />
        </g>
      ))}

      <g>
        <rect x="186" y="196" width="34" height="26" rx="8" fill="#e2634b" />
        <rect x="192" y="190" width="22" height="12" rx="5" fill="#ef7d64" />
        <circle cx="194" cy="220" r="4" fill="#3c4a52" />
        <circle cx="212" cy="220" r="4" fill="#3c4a52" />
      </g>
    </svg>
  )
}
